/**
 * Custom Reports Types (KAN-87)
 * TypeScript interfaces for the Custom Report Creation feature.
 * Reuses ColumnRule, EnumMapping, CalculatedColumn, ConditionalColumn from outputFormats.ts.
 */

import type {
  OutputFormat,
  ColumnRule,
  CsvOptions,
  EnumMapping,
  CalculatedColumn,
  ConditionalColumn,
  ConcatenatedColumn,
  SortSpec,
  SourceFieldDef,
} from './outputFormats';

// ============================================================
// Enums / Type Literals
// ============================================================

export type AssetClass = 'FX' | 'COMMODITY';

export type TemplateScope = 'USER' | 'SHARED';

export interface ClientUser {
  uid: string;
  displayName: string;
  email: string;
}
// ============================================================
// Sub-Models
// ============================================================

export type FilterOperator = 'equals' | 'not_equals';

export interface TradeFilters {
  // Trade date range
  tradeDateFrom: string | null;
  tradeDateTo: string | null;
  // Maturity date range
  maturityDateFrom: string | null;
  maturityDateTo: string | null;
  // Selection filters
  productTypes: string[];        // Spot, Forward, Forward Asiático (virtual)
  settlementTypes: string[];
  directions: string[];
  currency1: string | null;
  currency2: string | null;
  // Commodity-specific filters
  underlyings: string[];
  venues: string[];
  ourParty: string | null;
  counterparties: string[];
  workflowStates: string[];
  grouping1: string | null;
  grouping2: string | null;
  grouping3: string | null;
  isInternalTrade: boolean | null;
  // Not-operators for 6 fields (default: 'equals')
  ourPartyOperator: FilterOperator;
  counterpartyOperator: FilterOperator;
  currency1Operator: FilterOperator;
  currency2Operator: FilterOperator;
  grouping1Operator: FilterOperator;
  grouping2Operator: FilterOperator;
  grouping3Operator: FilterOperator;
  // Event-level report period (EVENT_LEVEL_REPORTS source type only)
  eventPeriodFrom: string | null;
  eventPeriodTo: string | null;
  eventTypes: string[];
}

// ============================================================
// Template Model
// ============================================================

export interface CustomReportTemplate {
  templateId?: string;
  name: string;
  description?: string;
  sourceType: 'TRADES' | 'EVENT_LEVEL_REPORTS';
  assetClass?: AssetClass;
  outputFormat: OutputFormat;
  scope: TemplateScope;
  isDefault: boolean;
  csvOptions?: CsvOptions;
  columns: ColumnRule[];
  enumMappings: EnumMapping[];
  calculatedColumns: CalculatedColumn[];
  conditionalColumns: ConditionalColumn[];
  concatenatedColumns?: ConcatenatedColumn[];
  sortBy?: SortSpec[];
  tradeFilters?: TradeFilters;
  sharedWith?: string[];
  isActive: boolean;
  createdAt?: string;
  createdBy?: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

/** Summary type for template list views */
export interface CustomReportTemplateSummary {
  templateId: string;
  name: string;
  description?: string;
  sourceType: 'TRADES' | 'EVENT_LEVEL_REPORTS';
  assetClass?: AssetClass;
  outputFormat: OutputFormat;
  scope: TemplateScope;
  isDefault: boolean;
  tradeFilters?: TradeFilters;
  sharedWith?: string[];
  ownerUid?: string;
  ownerDisplayName?: string;
  createdAt?: string;
  createdBy?: string;
  lastUpdatedAt?: string;
}

// ============================================================
// Source Schema
// ============================================================

export interface TradesSourceSchema {
  sourceType: 'TRADES' | 'EVENT_LEVEL_REPORTS';
  rowMode: 'INDIVIDUAL';
  coreFields: SourceFieldDef[];
  valuationFields: SourceFieldDef[];
  tradeFields: SourceFieldDef[];
  groupingAvailable: boolean;
}

// ============================================================
// Request / Response Models
// ============================================================

export interface CustomReportGenerateRequest {
  templateId: string;
  scope: TemplateScope;
  ownerUid?: string;
  sourceType: 'TRADES' | 'EVENT_LEVEL_REPORTS';
  assetClass?: AssetClass;
  params: Partial<TradeFilters> & Record<string, any>;
}

export interface FilterOptions {
  counterparties: string[];
  ourPartyValues: string[];
  workflowStates: string[];
  grouping1Values: string[];
  grouping2Values: string[];
  grouping3Values: string[];
  grouping1Label: string;
  grouping2Label: string;
  grouping3Label: string;
  productTypes: string[];
  currencies: string[];
  // Commodity-specific (omitted / empty for FX)
  underlyings?: string[];
  venues?: string[];
}

// ============================================================
// Default filter values (for new templates)
// ============================================================

export const DEFAULT_TRADE_FILTERS: TradeFilters = {
  tradeDateFrom: null,
  tradeDateTo: null,
  maturityDateFrom: null,
  maturityDateTo: null,
  productTypes: [],
  settlementTypes: [],
  directions: [],
  currency1: null,
  currency2: null,
  underlyings: [],
  venues: [],
  ourParty: null,
  counterparties: [],
  workflowStates: ['Booked', 'Confirmed', 'ContractSigned', 'Final'],
  grouping1: null,
  grouping2: null,
  grouping3: null,
  isInternalTrade: null,
  ourPartyOperator: 'equals',
  counterpartyOperator: 'equals',
  currency1Operator: 'equals',
  currency2Operator: 'equals',
  grouping1Operator: 'equals',
  grouping2Operator: 'equals',
  grouping3Operator: 'equals',
  eventPeriodFrom: null,
  eventPeriodTo: null,
  eventTypes: [],
};
